import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FootNav, Button, Badge, SearchField } from '@dreamtree-org/twreact-ui';
import { Home, Store, Wrench, ShoppingCart, User, Package, X, Instagram, Facebook, Youtube, Linkedin, Twitter, MessageCircle, ArrowRight } from 'lucide-react';
import { shopEntries } from '../lib/shop';
import { useStorefront } from '../StorefrontContext';
import { track } from '../lib/beacon';
import { safeHref } from '@dreamtree-org/landing-kit/schema';
import { isExternal, templateOf } from '../lib/design';
import { shownText } from '../lib/placeholders';
import { servicePages } from '../lib/servicePages';

/**
 * Storefront shell: announcement and utility bars, header (brand, search, cart,
 * account), desktop category nav, footer, a floating compare tray and the PWA
 * bottom nav (< md). The owner designs the bars, menu links and footer in the
 * store document's `chrome` (D66); empty lists keep the app's own links.
 */
export default function StoreLayout() {
  const sf = useStorefront();
  const location = useLocation();
  const home = location.pathname === '/';

  useEffect(() => {
    window.scrollTo(0, 0);
    track(location.pathname.startsWith('/shop') ? 'browse' : 'page_view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const at = (p) => (p === '/' ? location.pathname === '/' : location.pathname.startsWith(p));
  const cartCount = sf.cart.items.length;
  const showTray = sf.compareIds.length > 0 && (at('/') || at('/shop'));
  // The product page's sticky buy bar takes the bottom of the screen on phones.
  const buyBar = at('/p/') && templateOf(sf.doc, 'product').settings.sticky_buy_bar;

  // The services tab opens the first service page this store offers (D101): repair, else warranty.
  const [service] = servicePages('tab', sf.canVisit);
  const foot = [
    { id: 'home', label: 'Home', icon: <Home className="h-5 w-5" />, href: '/', active: at('/') },
    { id: 'shop', label: 'Shop', icon: <Store className="h-5 w-5" />, href: '/shop', active: at('/shop') || at('/p/') || at('/compare') },
    { id: 'repair', label: service.short, icon: <Wrench className="h-5 w-5" />, href: service.href, active: at('/repair') || at('/warranty') || at('/sell') },
    { id: 'cart', label: 'Cart', icon: <ShoppingCart className="h-5 w-5" />, href: '/cart', active: at('/cart') || at('/checkout'), badge: cartCount || undefined },
    { id: 'account', label: 'Account', icon: <User className="h-5 w-5" />, href: '/account', active: at('/account') },
  ].map(({ href, ...i }) => ({ ...i, to: href }));

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--app-bg)] text-[var(--app-text)]">
      <StoreHeader />

      {/* The landing page runs edge to edge (its sections carry their own gutters). */}
      <main className={`${home ? 'w-full' : 'mx-auto w-full max-w-screen-xl px-4 py-4 sm:px-6 sm:py-6'} flex-1 ${showTray ? 'pb-28' : 'pb-8'}`}>
        <Outlet />
      </main>

      {showTray ? <CompareTray /> : null}

      <StoreFooter />

      {/* On a product page with the sticky buy bar, the bar (and its spacer) replaces the bottom nav on phones.
          Otherwise the tab bar is fixed and the spacer keeps the footer clear of it (FootNav is md:hidden). */}
      {buyBar ? null : (
        <>
          <FootNav.Spacer className="md:hidden" />
          <FootNav items={foot} linkAs={Link} data-testid="footnav" />
        </>
      )}
    </div>
  );
}

/** Announcement and utility bars, then the header: brand, search, account and cart, category menu. */
export function StoreHeader() {
  const sf = useStorefront();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const logo = sf.appearance && sf.appearance.theme.logo ? sf.mediaUrl(sf.appearance.theme.logo) : null;

  useEffect(() => {
    setQ(location.pathname.startsWith('/shop') ? params.get('q') || '' : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/shop?q=${encodeURIComponent(term)}` : '/shop');
  };

  const at = (p) => (p === '/' ? location.pathname === '/' : location.pathname.startsWith(p));
  const cartCount = sf.cart.items.length;
  const entries = shopEntries(sf.shop);
  const here = `${location.pathname}${location.search}`;
  const searchLabel = `Search ${sf.shop.noun}, brands, models…`;
  const { announcement, header } = sf.chrome;
  const iconLink = 'relative inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-md px-2 text-sm hover:bg-primary-50';

  return (
    <>
      {/* Empty or landing-kit sample text: no bar (MERCH-55). */}
      {announcement.enabled && shownText(announcement.text) ? <AnnouncementBar {...announcement} /> : null}
      {header.utility_text ? (
        <div className="bg-primary-700 text-xs text-white">
          <div className="mx-auto flex max-w-screen-xl items-center justify-between gap-3 px-4 py-1.5 sm:px-6">
            <span className="truncate">{header.utility_text}</span>
            <a href="/admin" className="shrink-0 underline-offset-2 hover:underline">Staff sign-in</a>
          </div>
        </div>
      ) : null}

      <header className={`${header.sticky ? 'sticky top-0' : 'relative'} z-sticky border-b border-[var(--app-border)] bg-[var(--app-surface)] pt-safe`}>
        {/* Part of the header, so it never stacks over it while scrolling. */}
        {sf.previewing ? (
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-warning-100 px-4 py-2 text-sm text-warning-900" role="status">
            <span><strong>Previewing your draft.</strong> Shoppers still see the published page.</span>
            <Button size="sm" variant="outline" onClick={sf.exitPreview}>Exit preview</Button>
          </div>
        ) : null}
        <div className="mx-auto flex max-w-screen-xl items-center gap-3 px-4 py-2.5 sm:px-6 lg:gap-6">
          <Link to="/" className="flex min-w-0 items-center gap-2" aria-label={`${sf.brand.name} home`}>
            {logo ? (
              <img src={logo} alt={(sf.appearance.theme.logo && sf.appearance.theme.logo.alt) || sf.brand.name} className="h-9 w-auto max-w-32 shrink-0 object-contain" />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white">
                <Store className="h-5 w-5" aria-hidden="true" />
              </span>
            )}
            <span className="min-w-0">
              <span className="block truncate text-base font-bold leading-tight text-primary-700 sm:text-lg">{sf.brand.name}</span>
              <span className="hidden truncate text-xs opacity-60 lg:block">{sf.brand.tagline}</span>
            </span>
          </Link>

          {header.show_search ? (
          <form onSubmit={submit} className="hidden flex-1 items-center gap-2 md:flex" role="search">
            <div className="flex-1">
              <SearchField
                aria-label={`Search ${sf.shop.noun}`}
                placeholder={searchLabel}
                value={q}
                onChange={(e) => setQ(e.target.value)} />
            </div>
            <Button type="submit">Search</Button>
          </form>
          ) : null}

          <nav className="ml-auto flex items-center gap-1" aria-label="Account and cart">
            <NavLink to="/account/orders" className={`${iconLink} hidden lg:inline-flex`}>
              <Package className="h-5 w-5" aria-hidden="true" />
              <span>Orders</span>
            </NavLink>
            <NavLink to="/cart" className={iconLink} aria-label={`Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`}>
              <span className="relative inline-flex">
                <ShoppingCart className="h-5 w-5" aria-hidden="true" />
                {cartCount ? (
                  <span className="absolute -right-3 -top-2.5">
                    <Badge size="xs" variant="error">{cartCount}</Badge>
                  </span>
                ) : null}
              </span>
              <span className="hidden lg:ml-2 lg:inline">Cart</span>
            </NavLink>
            <NavLink to="/account" className={iconLink} aria-label="Account">
              <User className="h-5 w-5" aria-hidden="true" />
              <span className="hidden max-w-32 truncate lg:inline">{sf.session ? `Hi, ${(sf.session.name || 'there').split(' ')[0]}` : 'Sign in'}</span>
            </NavLink>
          </nav>
        </div>

        {header.show_search ? (
          <form onSubmit={submit} className="px-4 pb-2.5 md:hidden" role="search">
            <SearchField
              aria-label={`Search ${sf.shop.noun}`}
              placeholder={searchLabel}
              value={q}
              onChange={(e) => setQ(e.target.value)} />
          </form>
        ) : null}

        <nav className="hidden border-t border-[var(--app-border)] md:block" aria-label="Categories">
          <div className="mx-auto flex max-w-screen-xl items-center gap-1 overflow-x-auto px-4 text-sm sm:px-6">
            {header.category_nav
              ? entries.map((e) => (
                  <CatLink key={e.id} to={e.to} active={here === e.to}>
                    {e.label}
                  </CatLink>
                ))
              : null}
            {header.category_nav ? <span className="mx-2 h-5 w-px shrink-0 bg-[var(--app-border)]" aria-hidden="true" /> : null}
            {(header.nav_links.length ? header.nav_links : servicePages('header', sf.canVisit).map((p) => ({ label: p.nav, href: p.href }))).map((n) => (
              <CatLink key={`${n.label}-${n.href}`} to={n.href} active={!isExternal(n.href) && at(n.href.split('?')[0])}>{n.label}</CatLink>
            ))}
          </div>
        </nav>
      </header>
    </>
  );
}

/** The footer: about and socials, the owner's link columns (or the app's), account links, small print. */
export function StoreFooter() {
  const sf = useStorefront();
  const entries = shopEntries(sf.shop);
  const { footer } = sf.chrome;
  // The owner's about line, else the tagline; empty or landing-kit sample text is never shown (MERCH-55).
  const about = shownText(footer.about) || shownText(sf.brand.tagline);
  return (
    <footer className="border-t border-[var(--app-border)] bg-[var(--app-surface)]">
      <div className="mx-auto grid max-w-screen-xl gap-6 px-4 py-8 text-sm sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
        <div className="space-y-2">
          <div className="font-semibold">{sf.brand.name}</div>
          {about ? <p className="opacity-70" data-testid="footer-about">{about}</p> : null}
          {footer.socials.length ? (
            <ul className="flex flex-wrap gap-1 pt-1" aria-label="Follow us">
              {footer.socials.map((so) => {
                const Icon = SOCIAL_ICONS[so.network] || ArrowRight;
                return (
                  <li key={`${so.network}-${so.href}`}>
                    <a href={so.href} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL_LABELS[so.network] || so.network} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--app-border)] hover:border-primary-600 hover:text-primary-700">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </a>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        {footer.columns.length ? (
          footer.columns.map((c) => <FooterCol key={c.title} title={c.title} links={c.links.map((x) => [x.label, x.href])} />)
        ) : (
          <>
            <FooterCol title="Shop" links={[[entries[0] ? entries[0].label : 'Shop', entries[0] ? entries[0].to : '/shop'], ['Compare', '/compare'], ['Cart', '/cart']]} />
            <FooterCol title="Services" links={servicePages('footer', sf.canVisit).map((p) => [p.long, p.href])} />
          </>
        )}
        <FooterCol title="Account" links={[['My orders', '/account/orders'], ['Profile and addresses', '/account']]} extra={<a href="/admin" className="block py-1 opacity-80 hover:underline">Staff sign-in</a>} />
      </div>
      <div className="space-y-1 border-t border-[var(--app-border)] px-4 py-4 text-center text-xs opacity-60">
        {footer.note ? <div>{footer.note}</div> : null}
        <div>{sf.brand.name} · powered by MerchantPro</div>
      </div>
    </footer>
  );
}

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin, x: Twitter, whatsapp: MessageCircle };
const SOCIAL_LABELS = { instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', linkedin: 'LinkedIn', x: 'X', whatsapp: 'WhatsApp' };
// Announcement colours are theme tokens: the brand, the accent (secondary), or the page's text colour.
const TONES = {
  brand: 'bg-primary-600 text-white',
  accent: 'bg-secondary-600 text-white',
  dark: 'bg-[var(--app-text)] text-[var(--app-bg)]',
};

function AnnouncementBar({ text, href, tone }) {
  const body = <span className="truncate">{text}</span>;
  return (
    <div className={`${TONES[tone] || TONES.brand} text-center text-sm font-medium`} role="region" aria-label="Announcement">
      <div className="mx-auto flex max-w-screen-xl items-center justify-center gap-2 px-4 py-2 sm:px-6">
        {href ? (
          <SmartLink to={href} className="inline-flex min-h-6 max-w-full items-center gap-1 underline-offset-2 hover:underline">
            {body}
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
          </SmartLink>
        ) : body}
      </div>
    </div>
  );
}

/** A router link for storefront paths, a new-tab anchor for other sites. */
function SmartLink({ to, children, ...rest }) {
  // Saved docs are validated server-side; the Studio preview shows unsaved edits, so check here too.
  if (!safeHref(to) || String(to).includes('\\')) return <span {...rest}>{children}</span>;
  if (isExternal(to)) {
    return <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;
  }
  return <Link to={to} {...rest}>{children}</Link>;
}

function CatLink({ to, children, active }) {
  return (
    <SmartLink
      to={to}
      aria-current={active ? 'page' : undefined}
      className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 ${active ? 'border-primary-600 font-semibold text-primary-700' : 'border-transparent opacity-80 hover:opacity-100'}`}
    >
      {children}
    </SmartLink>
  );
}

function FooterCol({ title, links, extra }) {
  return (
    <div>
      <div className="mb-1 font-semibold">{title}</div>
      {links.map(([label, to]) => (
        <SmartLink key={`${label}-${to}`} to={to} className="block py-1 opacity-80 hover:underline">{label}</SmartLink>
      ))}
      {extra}
    </div>
  );
}

function CompareTray() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const units = sf.compareIds.map((id) => sf.byId(id)).filter(Boolean);
  return (
    <div className="fixed inset-x-0 bottom-20 z-sticky px-3 pb-safe md:bottom-4">
      <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-2 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] p-3 shadow-lg">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Compare ({sf.compareIds.length}/4)</div>
          <div className="truncate text-xs opacity-70">{units.map((u) => `${u.brand} ${u.model_name}`).join(' · ') || 'Selected items'}</div>
        </div>
        <Button size="sm" variant="ghost" leftIcon={<X className="h-4 w-4" />} onClick={() => sf.setCompare([])}>Clear</Button>
        <Button size="sm" disabled={sf.compareIds.length < 2} onClick={() => navigate(`/compare?ids=${sf.compareIds.join(',')}`)}>
          {sf.compareIds.length < 2 ? 'Pick one more' : 'Compare now'}
        </Button>
      </div>
    </div>
  );
}
